import React from "react";
import { App as AntdApp, Button, DatePicker, Form, Input, InputNumber, Modal, Progress, Select, Space, Typography, Upload } from "antd";
import { FilePdfOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "@/api/axios.js";

import { loadOrgTree } from "@modules/org/api/orgApi.js";

const { Text } = Typography;
const PDF_POINTS_PER_INCH = 72;
const MM_PER_INCH = 25.4;
const WEBP_QUALITY_MIN = 50;
const WEBP_QUALITY_MAX = 100;
const WEBP_QUALITY_DEFAULT = 90;
const RASTER_DPI_MIN = 72;
const RASTER_DPI_MAX = 300;
const RASTER_DPI_DEFAULT = 200;

let pdfLibPromise = null;
const loadPdfLib = async () => {
	if (!pdfLibPromise) pdfLibPromise = import("pdf-lib");
	return pdfLibPromise;
};

const ptToMm = (pt) => (pt * MM_PER_INCH) / PDF_POINTS_PER_INCH;
const ptToSafeMmInt = (pt) => Math.max(1, Math.floor(ptToMm(pt) + 0.0001));

/**
 * Modal per creare una variante "generica" a livello promo.
 * Come la creazione variante classica ma con selezione del canale.
 */
export default function CreateVariantInPromoModal({ open, onClose, promo, onVariantCreated }) {
	const { message } = AntdApp.useApp();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const parseSeqRef = React.useRef(0);

	const [loading, setLoading] = React.useState(false);
	const [progress, setProgress] = React.useState(0);
	const [canali, setCanali] = React.useState([]);

	const [filePdf, setFilePdf] = React.useState(null);
	const [webpQuality, setWebpQuality] = React.useState(WEBP_QUALITY_DEFAULT);
	const [rasterDpi, setRasterDpi] = React.useState(RASTER_DPI_DEFAULT);
	const [pdfWidthMm, setPdfWidthMm] = React.useState(null);
	const [pdfHeightMm, setPdfHeightMm] = React.useState(null);

	const isDimensionsValid = Number.isFinite(pdfWidthMm) && Number.isFinite(pdfHeightMm) && pdfWidthMm > 0 && pdfHeightMm > 0;
	const isQualityValid = Number.isFinite(webpQuality) && webpQuality >= WEBP_QUALITY_MIN && webpQuality <= WEBP_QUALITY_MAX;
	const isRasterDpiValid = Number.isFinite(rasterDpi) && rasterDpi >= RASTER_DPI_MIN && rasterDpi <= RASTER_DPI_MAX;
	const estimatedRasterSize = React.useMemo(() => {
		if (!isDimensionsValid || !isRasterDpiValid)
			return null;

		return {
			width: Math.max(1, Math.round((pdfWidthMm * rasterDpi) / MM_PER_INCH)),
			height: Math.max(1, Math.round((pdfHeightMm * rasterDpi) / MM_PER_INCH))
		};
	}, [isDimensionsValid, isRasterDpiValid, pdfWidthMm, pdfHeightMm, rasterDpi]);

	React.useEffect(() => {
		if (!open || !promo?.cooperativaId) return;
		(async () => {
			const items = await loadOrgTree();
			const opts = items
				.filter(item => item.type === "CANALE" && String(item.parentId) === String(promo.cooperativaId))
				.map(item => ({ label: item.name, value: String(item._id) }));
			setCanali(opts);
		})();
	}, [open, promo?.cooperativaId]);

	React.useEffect(() => {
		if (!open) return;
		form.setFieldsValue({
			range: promo?.sellOutFrom && promo?.sellOutTo
				? [dayjs(promo.sellOutFrom), dayjs(promo.sellOutTo)]
				: undefined
		});
	}, [form, open, promo?.sellOutFrom, promo?.sellOutTo]);

	// Con destroyOnHidden il Form si rimonta a ogni apertura; resettiamo solo lo stato esterno.
	React.useEffect(() => {
		if (!open) {
			parseSeqRef.current += 1;
			setFilePdf(null);
			setWebpQuality(WEBP_QUALITY_DEFAULT);
			setRasterDpi(RASTER_DPI_DEFAULT);
			setPdfWidthMm(null);
			setPdfHeightMm(null);
			setLoading(false);
			setProgress(0);
		}
	}, [open]);

	React.useEffect(() => {
		if (!open) return;

		let cancelled = false;
		api.get("/api/settings")
			.then(({ data }) => {
				if (cancelled) return;
				const nextDpi = Number(data?.settings?.flyerPages?.ocrDpi);
				if (Number.isFinite(nextDpi) && nextDpi >= RASTER_DPI_MIN && nextDpi <= RASTER_DPI_MAX)
					setRasterDpi(nextDpi);
			})
			.catch(() => null);

		return () => {
			cancelled = true;
		};
	}, [open]);

	const parseFirstPageSizeMm = async (file) => {
		const arrayBuffer = await file.arrayBuffer();
		try {
			const { PDFDocument } = await loadPdfLib();
			const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
			if (pdfDoc.getPageCount() <= 0) return null;
			const box = pdfDoc.getPage(0).getMediaBox();
			if (!box || !Number.isFinite(box.width) || !Number.isFinite(box.height) || box.width <= 0 || box.height <= 0) return null;
			return { widthMm: ptToSafeMmInt(box.width), heightMm: ptToSafeMmInt(box.height) };
		}
		catch {
			return null;
		}
	};

	const beforeUpload = async (file) => {
		const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
		if (!isPdf) {
			message.error("Solo file PDF sono supportati");
			return false;
		}
		const seq = parseSeqRef.current + 1;
		parseSeqRef.current = seq;
		setFilePdf(file);
		setPdfWidthMm(null);
		setPdfHeightMm(null);
		try {
			const size = await parseFirstPageSizeMm(file);
			if (parseSeqRef.current !== seq) return false;
			if (size) {
				setPdfWidthMm(size.widthMm);
				setPdfHeightMm(size.heightMm);
			} else {
				message.warning("Dimensioni PDF non rilevate. Inseriscile manualmente.");
			}
		}
		catch {
			message.warning("Impossibile leggere le dimensioni del PDF. Inseriscile manualmente.");
		}
		return false;
	};

	const handleCreate = async () => {
		try {
			const values = await form.validateFields();
			if (!filePdf) return message.warning("Seleziona un file PDF");
			if (!isDimensionsValid) return message.warning("Inserisci larghezza/altezza PDF valide");
			if (!isQualityValid) return message.warning(`Qualità WebP non valida (${WEBP_QUALITY_MIN}–${WEBP_QUALITY_MAX})`);
			if (!isRasterDpiValid) return message.warning(`DPI OCR non valido (${RASTER_DPI_MIN}–${RASTER_DPI_MAX})`);

			setLoading(true);
			setProgress(20);

			const promoId = promo._id || promo.id;
			const formData = new FormData();
			formData.append("pdf", filePdf, filePdf.name);
			formData.append("name", values.name?.trim() || "");
			formData.append("canaleId", values.canaleId || "");
			formData.append("sellOutFrom", values.range?.[0]?.format("YYYY-MM-DD") || "");
			formData.append("sellOutTo", values.range?.[1]?.format("YYYY-MM-DD") || "");
			formData.append("pdfWidthMm", String(pdfWidthMm));
			formData.append("pdfHeightMm", String(pdfHeightMm));
			formData.append("webpQuality", String(webpQuality));
			formData.append("rasterDpi", String(rasterDpi));

			setProgress(40);
			const { data } = await api.post(`/api/promos/${promoId}/variant`, formData);
			setProgress(100);

			if (data.ok) {
				message.success("Variante creata!");
				onVariantCreated?.(data.variant || data);
				onClose?.();
				const vid = data.id || data.variant?.id || data.variant?._id;
				if (vid) navigate(`/variant-preprocess/${vid}`);
			} else {
				message.error(data.error || "Errore creazione variante");
			}
		}
		catch (err) {
			if (err?.errorFields) return;
			message.error(err?.response?.data?.error || err.message || "Errore creazione variante");
		}
		finally {
			setLoading(false);
		}
	};

	const handleCancel = () => { if (!loading) onClose?.(); };
	const canSubmit = filePdf && isDimensionsValid && isQualityValid && isRasterDpiValid;

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			title="Nuova Variante"
			width={780}
			maskClosable={!loading}
			closable={!loading}
			className="sis-modal"
			footer={null}
			destroyOnHidden
		>
			<div style={{ display: "flex", gap: 0, minHeight: 0 }}>
				<div style={{ flex: "0 0 50%", paddingRight: 20, display: "flex", flexDirection: "column" }}>
					<Text type="secondary" style={{ marginBottom: 12 }}>Promo: {promo?.name}</Text>
					<Form form={form} layout="vertical" style={{ flex: 1 }}>
						<Form.Item label="Nome Variante" name="name" rules={[{ required: true, message: "Inserisci un nome" }]}>
							<Input placeholder="Es: Variante Nord" autoFocus />
						</Form.Item>
						<Form.Item label="Canale" name="canaleId" rules={[{ required: true, message: "Seleziona un canale" }]}>
							<Select disabled={!canali.length} placeholder="Seleziona canale" options={canali} showSearch
								filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
						</Form.Item>
						<Form.Item label="Range di date" name="range" rules={[{ required: true, message: "Seleziona il periodo" }]}>
							<DatePicker.RangePicker format="DD/MM/YYYY" placeholder={["Dal", "Al"]} style={{ width: "100%" }} />
						</Form.Item>
					</Form>
				</div>

				<div style={{ flex: "0 0 1px", background: "#f0f0f0", alignSelf: "stretch" }} />

				<div style={{ flex: "1 1 50%", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 12 }}>
					<Upload.Dragger beforeUpload={beforeUpload} accept=".pdf,application/pdf" showUploadList={false} disabled={loading} style={{ background: "rgba(255,255,255,0.6)" }}>
						<div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 5, height: "100%" }}>
							<FilePdfOutlined style={{ fontSize: 36, color: filePdf ? "#52c41a" : "#ff4d4f" }} />
							<div style={{ textAlign: "left", lineHeight: 1.4 }}>
								<div style={{ fontSize: 13, fontWeight: 500, color: filePdf ? "#52c41a" : undefined }}>
									{filePdf ? filePdf.name : "Trascina il PDF qui"}
								</div>
								<div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
									{filePdf ? "Clicca per sostituire" : "Oppure clicca per selezionare"}
								</div>
							</div>
						</div>
					</Upload.Dragger>

					<Form layout="vertical">
						<Form.Item label="Dimensioni PDF" style={{ marginBottom: 12 }}>
							<Space.Compact style={{ width: "100%" }}>
								<InputNumber min={1} step={1} precision={0} value={Number.isFinite(pdfWidthMm) ? pdfWidthMm : undefined}
									onChange={(v) => setPdfWidthMm(typeof v === "number" ? v : null)} style={{ width: "50%" }} disabled={loading || !filePdf} placeholder="Larghezza" suffix="mm" />
								<InputNumber min={1} step={1} precision={0} value={Number.isFinite(pdfHeightMm) ? pdfHeightMm : undefined}
									onChange={(v) => setPdfHeightMm(typeof v === "number" ? v : null)} style={{ width: "50%" }} disabled={loading || !filePdf} placeholder="Altezza" suffix="mm" />
							</Space.Compact>
						</Form.Item>
						<Form.Item label={`Qualità WebP (${WEBP_QUALITY_MIN}–${WEBP_QUALITY_MAX})`} style={{ marginBottom: 12 }}>
							<InputNumber min={WEBP_QUALITY_MIN} max={WEBP_QUALITY_MAX} step={1} precision={0} value={Number.isFinite(webpQuality) ? webpQuality : undefined}
								onChange={(v) => setWebpQuality(typeof v === "number" ? v : NaN)} style={{ width: "100%" }} disabled={loading || !filePdf} />
						</Form.Item>
						<Form.Item
							label={`DPI OCR pagine (${RASTER_DPI_MIN}–${RASTER_DPI_MAX})`}
							extra={
								estimatedRasterSize
									? `Dimensione stimata pagine: circa ${estimatedRasterSize.width} x ${estimatedRasterSize.height} px.`
									: `Default impostazioni ${RASTER_DPI_DEFAULT} DPI. La stima comparirà dopo il rilevamento delle dimensioni PDF.`
							}
							style={{ marginBottom: 0 }}
						>
							<InputNumber
								min={RASTER_DPI_MIN}
								max={RASTER_DPI_MAX}
								step={1}
								precision={0}
								value={Number.isFinite(rasterDpi) ? rasterDpi : undefined}
								onChange={(v) => setRasterDpi(typeof v === "number" ? v : NaN)}
								style={{ width: "100%" }}
								disabled={loading || !filePdf}
							/>
						</Form.Item>
					</Form>
				</div>
			</div>

			{loading && <Progress percent={progress} status="active" style={{ marginTop: 16 }} />}

			<Space style={{ width: "100%", justifyContent: "flex-end", marginTop: 20 }}>
				<Button onClick={handleCancel} disabled={loading}>Annulla</Button>
				<Button type="primary" onClick={handleCreate} loading={loading} disabled={!canSubmit} icon={<CopyOutlined />}>
					Crea Variante
				</Button>
			</Space>
		</Modal>
	);
}
